import type { ExtendedNodeType, NodeColorCategory, ExtendedEdgeType } from '@veodiagram/shared';

/**
 * Color palette for diagram nodes.
 * Each category has background, border, and text colors for light and dark modes.
 */
export const colorPalette: Record<NodeColorCategory, {
  light: { bg: string; border: string; text: string };
  dark: { bg: string; border: string; text: string };
}> = {
  primary: {
    light: { bg: '#e1f5ff', border: '#3b82f6', text: '#1e40af' },
    dark: { bg: '#1e3a5f', border: '#60a5fa', text: '#bfdbfe' },
  },
  success: {
    light: { bg: '#e1ffe1', border: '#22c55e', text: '#166534' },
    dark: { bg: '#1a3d1a', border: '#4ade80', text: '#bbf7d0' },
  },
  warning: {
    light: { bg: '#fff1e1', border: '#f59e0b', text: '#92400e' },
    dark: { bg: '#3d2e1a', border: '#fbbf24', text: '#fef3c7' },
  },
  danger: {
    light: { bg: '#ffe1e1', border: '#ef4444', text: '#991b1b' },
    dark: { bg: '#3d1a1a', border: '#f87171', text: '#fecaca' },
  },
  info: {
    light: { bg: '#e9d5ff', border: '#a855f7', text: '#6b21a8' },
    dark: { bg: '#2e1a3d', border: '#c084fc', text: '#e9d5ff' },
  },
  neutral: {
    light: { bg: '#f1f5f9', border: '#64748b', text: '#334155' },
    dark: { bg: '#1e293b', border: '#94a3b8', text: '#cbd5e1' },
  },
};

/**
 * Maps node types to their semantic color category.
 */
export const nodeTypeToColorCategory: Record<ExtendedNodeType, NodeColorCategory> = {
  // Services and APIs (primary - blue)
  service: 'primary',
  api: 'primary',
  gateway: 'primary',
  frontend: 'primary',
  container: 'primary',
  component: 'primary',
  process: 'primary',

  // Success/Output (success - green)
  deployment: 'success',
  softwareSystem: 'success',
  end: 'success',

  // Data stores (warning - yellow/orange)
  database: 'warning',
  storage: 'warning',
  cache: 'warning',
  queue: 'warning',
  dataStore: 'warning',

  // External/Untrusted (danger - red)
  external: 'danger',
  externalEntity: 'danger',
  trustBoundary: 'danger',

  // Integrations (info - purple)
  person: 'info',
  entity: 'info',

  // Infrastructure (neutral - gray)
  server: 'neutral',
  loadBalancer: 'neutral',
  cdn: 'neutral',
  firewall: 'neutral',
  codeBlock: 'neutral',
  decision: 'neutral',
  start: 'neutral',

  // Issue #414: Call graph node types
  function: 'primary',
  method: 'primary',
  arrow: 'primary',
  constructor: 'info',
  entry: 'success',
  critical: 'danger',
  hotspot: 'warning',
  callGraphNode: 'primary',
  // Issue #500/#565: Import graph node types
  importGraphNode: 'neutral',
  importGroupNode: 'neutral',
};

/**
 * Shape mappings for different node types.
 */
export const nodeTypeToShape: Record<ExtendedNodeType, string> = {
  // Rectangles
  service: 'rounded',
  api: 'rounded',
  container: 'rounded',
  component: 'rounded',
  frontend: 'rounded',
  gateway: 'rounded',
  softwareSystem: 'rounded',
  codeBlock: 'rectangle',

  // Cylinders (for databases)
  database: 'cylinder',
  storage: 'cylinder',
  cache: 'cylinder',
  dataStore: 'cylinder',

  // Hexagons
  queue: 'hexagon',

  // Diamonds
  decision: 'diamond',

  // Circles
  start: 'circle',
  end: 'circle',
  person: 'circle',

  // Other shapes
  deployment: 'rounded',
  external: 'rectangle',
  externalEntity: 'rectangle',
  trustBoundary: 'rectangle',
  server: 'rectangle',
  loadBalancer: 'rectangle',
  cdn: 'rectangle',
  firewall: 'rectangle',
  entity: 'rounded',
  process: 'rounded',

  // Issue #414: Call graph node types
  function: 'rounded',
  method: 'rounded',
  arrow: 'rounded',
  constructor: 'rounded',
  entry: 'rounded',
  critical: 'rounded',
  hotspot: 'hexagon',
  callGraphNode: 'rounded',
  importGraphNode: 'rounded',
  importGroupNode: 'rounded',
};

/**
 * Get colors for a node type.
 */
export function getNodeColors(
  nodeType: ExtendedNodeType,
  isDark: boolean = false
): { bg: string; border: string; text: string } {
  const category = nodeTypeToColorCategory[nodeType] || 'neutral';
  return isDark ? colorPalette[category].dark : colorPalette[category].light;
}

/**
 * Get Tailwind classes for a node type.
 */
export function getNodeTailwindClasses(
  nodeType: ExtendedNodeType,
  selected: boolean = false
): string {
  const baseClasses = 'px-4 py-3 rounded-lg border-2 min-w-[150px] transition-all duration-200';
  const selectedClasses = selected ? 'ring-2 ring-primary ring-offset-2 shadow-lg' : '';

  // Using Map to avoid conflict with reserved 'constructor' property
  const colorMap = new Map<ExtendedNodeType, string>([
    ['service', 'bg-blue-50 border-blue-400 text-blue-900 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-100'],
    ['api', 'bg-blue-50 border-blue-400 text-blue-900 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-100'],
    ['database', 'bg-amber-50 border-amber-400 text-amber-900 dark:bg-amber-950 dark:border-amber-500 dark:text-amber-100'],
    ['storage', 'bg-amber-50 border-amber-400 text-amber-900 dark:bg-amber-950 dark:border-amber-500 dark:text-amber-100'],
    ['cache', 'bg-amber-50 border-amber-400 text-amber-900 dark:bg-amber-950 dark:border-amber-500 dark:text-amber-100'],
    ['queue', 'bg-orange-50 border-orange-400 text-orange-900 dark:bg-orange-950 dark:border-orange-500 dark:text-orange-100'],
    ['external', 'bg-red-50 border-red-400 text-red-900 dark:bg-red-950 dark:border-red-500 dark:text-red-100'],
    ['deployment', 'bg-green-50 border-green-400 text-green-900 dark:bg-green-950 dark:border-green-500 dark:text-green-100'],
    ['person', 'bg-purple-50 border-purple-400 text-purple-900 dark:bg-purple-950 dark:border-purple-500 dark:text-purple-100'],
    ['frontend', 'bg-cyan-50 border-cyan-400 text-cyan-900 dark:bg-cyan-950 dark:border-cyan-500 dark:text-cyan-100'],
    // Issue #414: Call graph node types
    ['function', 'bg-blue-50 border-blue-400 text-blue-900 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-100'],
    ['method', 'bg-blue-50 border-blue-400 text-blue-900 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-100'],
    ['arrow', 'bg-blue-50 border-blue-400 text-blue-900 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-100'],
    ['constructor', 'bg-purple-50 border-purple-400 text-purple-900 dark:bg-purple-950 dark:border-purple-500 dark:text-purple-100'],
    ['entry', 'bg-green-50 border-green-400 text-green-900 dark:bg-green-950 dark:border-green-500 dark:text-green-100'],
    ['critical', 'bg-red-50 border-red-400 text-red-900 dark:bg-red-950 dark:border-red-500 dark:text-red-100'],
    ['hotspot', 'bg-orange-50 border-orange-400 text-orange-900 dark:bg-orange-950 dark:border-orange-500 dark:text-orange-100'],
    ['callGraphNode', 'bg-blue-50 border-blue-400 text-blue-900 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-100'],
  ]);

  const colorClasses = colorMap.get(nodeType) || 'bg-slate-50 border-slate-400 text-slate-900 dark:bg-slate-800 dark:border-slate-500 dark:text-slate-100';

  return `${baseClasses} ${colorClasses} ${selectedClasses}`.trim();
}

/**
 * Edge style configurations.
 */
export const edgeStyles: Record<ExtendedEdgeType, {
  strokeDasharray?: string;
  animated?: boolean;
  strokeWidth: number;
  color: string;
}> = {
  default: { strokeWidth: 2, color: '#64748b' },
  http: { strokeWidth: 2, color: '#3b82f6' },
  async: { strokeWidth: 2, color: '#f59e0b', animated: true },
  database: { strokeWidth: 2, color: '#eab308', strokeDasharray: '5,5' },
  file: { strokeWidth: 2, color: '#22c55e', strokeDasharray: '3,3' },
  sync: { strokeWidth: 2, color: '#3b82f6' },
  bidirectional: { strokeWidth: 2, color: '#8b5cf6' },
  dependency: { strokeWidth: 1, color: '#94a3b8', strokeDasharray: '5,5' },
  inheritance: { strokeWidth: 2, color: '#64748b' },
  composition: { strokeWidth: 2, color: '#1e293b' },
  aggregation: { strokeWidth: 2, color: '#475569' },
  uses: { strokeWidth: 2, color: '#3b82f6' },
  publishes: { strokeWidth: 2, color: '#22c55e' },
  subscribes: { strokeWidth: 2, color: '#f59e0b' },
  reads: { strokeWidth: 1, color: '#3b82f6', strokeDasharray: '3,3' },
  writes: { strokeWidth: 2, color: '#ef4444' },
  calls: { strokeWidth: 2, color: '#6366f1' },
};

/**
 * Icons for different node types (using emoji for simplicity).
 */
export const nodeTypeIcons: Record<ExtendedNodeType, string> = {
  service: '⚙️',
  api: '🔌',
  gateway: '🚪',
  frontend: '🖥️',
  container: '📦',
  component: '🧩',
  process: '▶️',
  deployment: '🚀',
  softwareSystem: '🏢',
  end: '🏁',
  database: '🗄️',
  storage: '💾',
  cache: '⚡',
  queue: '📨',
  dataStore: '📁',
  external: '🌐',
  externalEntity: '👤',
  trustBoundary: '🔒',
  person: '👤',
  entity: '📋',
  server: '🖥️',
  loadBalancer: '⚖️',
  cdn: '🌍',
  firewall: '🛡️',
  codeBlock: '📝',
  decision: '❓',
  start: '▶️',

  // Issue #414: Call graph node types
  function: '𝑓',
  method: '𝑚',
  arrow: '→',
  constructor: '🔧',
  entry: '▶️',
  critical: '⚠️',
  hotspot: '🔥',
  callGraphNode: '⚙️',
  importGraphNode: '📄',
  importGroupNode: '📁',
};
